import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import type {
  OnboardingConfig,
  OnboardingConfigUpsertInput,
  OnboardingData,
  OnboardingDocSaveInput,
  OnboardingDocument,
  OnboardingSaveInput,
} from '@cp3/shared';
import { functions, storage } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';

interface OnboardingBundle {
  data: OnboardingData | null;
  documents: OnboardingDocument[];
}

export function useOnboarding() {
  const { user } = useUser();
  return useQuery<OnboardingBundle>({
    queryKey: ['onboarding', user?.uid] as const,
    enabled: !!user,
    staleTime: 5_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, OnboardingBundle>(functions, 'getOnboarding');
      const r = await fn({});
      return r.data;
    },
  });
}

export function useSaveOnboarding() {
  return useMutation({
    mutationFn: async (input: OnboardingSaveInput) => {
      const fn = httpsCallable<OnboardingSaveInput, { ok: true }>(functions, 'saveOnboarding');
      const r = await fn(input);
      return r.data;
    },
    // No optimistic cache write — the wizard already holds the live form
    // state in component memory. Invalidation happens on close/reopen.
  });
}

export function useCompressOnboarding() {
  const { user } = useUser();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable<unknown, {
        ok: true;
        knowledgeBase: string;
        introPhrase: string;
        outroPhrase: string;
        conversationStylePrompt: string;
      }>(functions, 'compressOnboardingToPrompts');
      const r = await fn({});
      return r.data;
    },
    onSuccess: () => {
      // Onboarding doc gets `lastCompressedAt`; phoneSettings gets the
      // generated fields. Both queries refetch.
      void qc.invalidateQueries({ queryKey: ['onboarding', user?.uid] });
      void qc.invalidateQueries({ queryKey: ['phoneSettings', user?.uid] });
    },
  });
}

export function useUploadOnboardingDoc() {
  const { user } = useUser();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error('Nicht angemeldet.');
      const docId = crypto.randomUUID();
      const objectPath = `onboarding/${user.uid}/${docId}-${file.name}`;
      const sref = storageRef(storage, objectPath);
      await uploadBytes(sref, file, { contentType: file.type });
      const meta: OnboardingDocSaveInput = {
        id: docId,
        fileName: file.name,
        mimeType: file.type as OnboardingDocSaveInput['mimeType'],
        storagePath: objectPath,
        sizeBytes: file.size,
      };
      const fn = httpsCallable<OnboardingDocSaveInput, { ok: true; extractedChars: number }>(
        functions,
        'registerOnboardingDoc',
      );
      const r = await fn(meta);
      return { docId, ...r.data };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['onboarding', user?.uid] });
    },
  });
}

export function useDeleteOnboardingDoc() {
  const { user } = useUser();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const fn = httpsCallable<{ id: string }, { ok: true }>(functions, 'deleteOnboardingDoc');
      const r = await fn({ id });
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['onboarding', user?.uid] });
    },
  });
}

// ── Admin: Onboarding-Konfiguration ────────────────────────────────────────

export function useOnboardingConfig() {
  const { user } = useUser();
  return useQuery<{ config: OnboardingConfig & { steps: OnboardingConfig['steps'] } }>({
    queryKey: ['onboarding-config'] as const,
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { config: OnboardingConfig }>(
        functions,
        'getOnboardingConfig',
      );
      const r = await fn({});
      return r.data as { config: OnboardingConfig & { steps: OnboardingConfig['steps'] } };
    },
  });
}

export function useUpsertOnboardingConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: OnboardingConfigUpsertInput) => {
      const fn = httpsCallable<OnboardingConfigUpsertInput, { ok: true }>(
        functions,
        'upsertOnboardingConfig',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['onboarding-config'] });
    },
  });
}
